import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { changePassword, goToDashboard } from '../../../actions/changePassword';
import { isAuthenticated } from '../../../actions/login';
import { AppState } from '../../../reducers';
import { Form } from '@design-system/molecules/form';
import { PasswordField } from '@design-system/molecules/password-field';
import * as S from '../auth.styles';
import { z } from 'zod';
import { useToastContext } from '@design-system/molecules/toast/Toast';
import { useFormContext } from 'react-hook-form';
import { createSigninPasswordSchema } from '@design-system/molecules/password-field/password-field.validation';

function useChangePasswordErrorToast() {
  const intl = useIntl();
  const { showToast } = useToastContext();
  const loginData = useSelector((state: AppState) => state.login);

  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (loginData.failed) {
      if (loginData.userData.message === 'wrongPW') {
        setLoginError(intl.formatMessage({ id: 'dialog.change.password.info.wrong.password' }));
      } else if (loginData.userData.message === 'authenticationFailed') {
        setLoginError(intl.formatMessage({ id: 'dialog.change.password.info.empty.password' }));
      } else if (loginData.userData.message === 'undefinedResponse') {
        setLoginError(intl.formatMessage({ id: 'data.fetching.bad.request"' }));
      }
    } else {
      setLoginError('');
    }
  }, [loginData, intl]);

  useEffect(() => {
    if (loginError) {
      showToast({
        title: intl.formatMessage({ id: 'components.toast.error.title' }),
        message: loginError,
        variant: 'error',
      });
    }
  }, [loginError, showToast, intl]);
}

const resetPasswordSchema = z.object({
  password: createSigninPasswordSchema(),
  newPassword: createSigninPasswordSchema(),
});

type FormData = z.infer<typeof resetPasswordSchema>;

const FormContent = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: AppState) => state.login.isLoading);
  const userName = useSelector((state: AppState) => state.login.userData.userName);
  const intl = useIntl();

  const { watch } = useFormContext<FormData>();

  const password = watch('password');
  const newPassword = watch('newPassword');

  useChangePasswordErrorToast();

  const cancel = () => {
    dispatch(goToDashboard());
  };

  return (
    <S.AuthContainer fixedHeight={448}>
      <S.AuthHeader variant="h2" as="h2">
        <FormattedMessage
          id="login.change.password.title"
          values={{
            userName,
            subTitle: (chunks: React.ReactNode) => (
              <S.AuthHeaderGreen as="span" variant="h2">
                {chunks}
              </S.AuthHeaderGreen>
            ),
          }}
        />
      </S.AuthHeader>
      <PasswordField
        name="password"
        label={intl.formatMessage({ id: 'dialog.change.password.label.current.pass' })}
        placeholder="••••••••••••••"
        hintConfig={{ capslockOn: intl.formatMessage({ id: 'common.inputHint.password' }) }}
      />
      <PasswordField
        name="newPassword"
        label={intl.formatMessage({ id: 'dialog.change.set.password.label.new.pass' })}
        placeholder="••••••••••••••"
        hintConfig={{ capslockOn: intl.formatMessage({ id: 'common.inputHint.password' }) }}
      />
      <S.BackupSpace />
      <S.AuthButton
        type="submit"
        isDisabled={!newPassword?.trim() || !password?.trim()}
        isLoading={isLoading}
        variant="primary"
        size="large"
      >
        <FormattedMessage id="dialog.change.password.button.submit.change" />
      </S.AuthButton>
      <S.AuthButton type="button" onClick={cancel} variant="text" size="large">
        <FormattedMessage id="common.button.cancel" />
      </S.AuthButton>
    </S.AuthContainer>
  );
};

export const ChangePassword = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    isAuthenticated();
  }, []);

  const handleSubmit = (data: FormData) => {
    const { password, newPassword } = data;

    dispatch(changePassword(password, newPassword));
  };

  return (
    <Form onSubmit={handleSubmit} config={{ mode: 'onBlur' }}>
      <FormContent />
    </Form>
  );
};
